import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect, ReactNode } from 'react';
import GlobalNavButtons from '../ui/navigation/GlobalNavButtons';
import {
  RYASKOV_BACK_REQUEST_EVENT,
  type RyaskovBackRequestDetail
} from '../nativeBridge/nativeBridge';

interface BackButtonState {
  show: boolean;
  onBack: (() => void) | null;
  transparent: boolean;
  /** Кнопка в шапке страницы — глобальный портал не показываем */
  embeddedInHeader?: boolean;
}

interface MenuButtonState {
  show: boolean;
  onMenu: (() => void) | null;
  transparent: boolean;
}

interface NavigationState {
  back: BackButtonState;
  menu: MenuButtonState;
  /** Длина стека слоёв «Назад». > 1 — поверх базового слоя модалка/карточка. */
  backStackLength: number;
}

interface BackLayer {
  onBack: () => void;
  transparent: boolean;
  embeddedInHeader?: boolean;
}

interface NavigationContextValue {
  /** Показать кнопку назад (заменяет текущий слой) */
  showBack: (onBack: () => void, transparent?: boolean, embeddedInHeader?: boolean) => void;
  /** Временно повесить обработчик «Назад» поверх текущего (для модалок). При закрытии вызвать popBackLayer. */
  pushBackLayer: (onBack: () => void, transparent?: boolean, embeddedInHeader?: boolean) => void;
  /** Убрать верхний слой и вернуть предыдущий обработчик «Назад». */
  popBackLayer: () => void;
  /** Скрыть кнопку назад */
  hideBack: () => void;
  /** Показать кнопку меню */
  showMenu: (onMenu: () => void, transparent?: boolean) => void;
  /** Скрыть кнопку меню */
  hideMenu: () => void;
  /** Текущее состояние */
  state: NavigationState;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

// Стабильная заглушка, чтобы при отсутствии провайдера не создавать новый объект каждый рендер
// (иначе эффекты с зависимостью от контекста зацикливаются)
const fallbackContextValue: NavigationContextValue = {
  showBack: () => {},
  pushBackLayer: () => {},
  popBackLayer: () => {},
  hideBack: () => {},
  showMenu: () => {},
  hideMenu: () => {},
  state: {
    back: { show: false, onBack: null, transparent: false },
    menu: { show: false, onMenu: null, transparent: false },
    backStackLength: 0
  }
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  return context ?? fallbackContextValue;
};

interface NavigationProviderProps {
  children: ReactNode;
}

const initialState: NavigationState = {
  back: { show: false, onBack: null, transparent: false },
  menu: { show: false, onMenu: null, transparent: false },
  backStackLength: 0
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const [state, setState] = useState<NavigationState>(initialState);

  const onMenuRef = useRef<(() => void) | null>(null);
  // Стек слоёв «Назад»: верхний — активный (карточка/модалка), ниже — окно объекта и т.д.
  const backStackRef = useRef<BackLayer[]>([]);

  const showBack = useCallback((onBack: () => void, transparent = false, embeddedInHeader = false) => {
    backStackRef.current = [{ onBack, transparent, embeddedInHeader }];
    setState(prev => ({
      ...prev,
      back: { show: true, onBack, transparent, embeddedInHeader },
      backStackLength: 1
    }));
  }, []);

  const pushBackLayer = useCallback((onBack: () => void, transparent = false, embeddedInHeader = false) => {
    backStackRef.current.push({ onBack, transparent, embeddedInHeader });
    const len = backStackRef.current.length;
    const top = backStackRef.current[len - 1];
    setState(prev => ({
      ...prev,
      back: {
        show: true,
        onBack: top.onBack,
        transparent: top.transparent,
        embeddedInHeader: top.embeddedInHeader,
      },
      backStackLength: len
    }));
  }, []);

  const popBackLayer = useCallback(() => {
    const stack = backStackRef.current;
    if (stack.length > 0) stack.pop();
    const len = stack.length;
    const top = len > 0 ? stack[len - 1] : null;
    if (top) {
      setState(prev => ({
        ...prev,
        back: {
          show: true,
          onBack: top.onBack,
          transparent: top.transparent,
          embeddedInHeader: top.embeddedInHeader,
        },
        backStackLength: len,
      }));
    } else {
      setState(prev => ({
        ...prev,
        back: { show: false, onBack: null, transparent: false, embeddedInHeader: false },
        backStackLength: 0
      }));
    }
  }, []);

  const hideBack = useCallback(() => {
    backStackRef.current = [];
    setState(prev => ({
      ...prev,
      back: { show: false, onBack: null, transparent: false, embeddedInHeader: false },
      backStackLength: 0
    }));
  }, []);

  const showMenu = useCallback((onMenu: () => void, transparent = false) => {
    onMenuRef.current = onMenu;
    setState(prev => ({
      ...prev,
      menu: { show: true, onMenu, transparent }
    }));
  }, []);

  const hideMenu = useCallback(() => {
    onMenuRef.current = null;
    setState(prev => ({
      ...prev,
      menu: { show: false, onMenu: null, transparent: false }
    }));
  }, []);

  // Обработчик для визуальной кнопки "Назад".
  // Сначала даём нативной обёртке (APK) перехватить: мост onBackRequest или событие ryaskov-back-request.
  const handleBack = useCallback(() => {
    let requestDefault = false;
    const detail: RyaskovBackRequestDetail = {
      requestDefault: () => { requestDefault = true; }
    };
    try {
      window.dispatchEvent(new CustomEvent(RYASKOV_BACK_REQUEST_EVENT, { detail }));
    } catch {
      // игнорируем ошибки в слушателях
    }
    const handledByBridge = window.RyaskovNativeBridge?.onBackRequest?.() === true;
    if (!handledByBridge || requestDefault) {
      const stack = backStackRef.current;
      const top = stack.length > 0 ? stack[stack.length - 1] : null;
      if (top) {
        const bridge = window.RyaskovNativeBridge;
        if (bridge) bridge.backWasHandled = true;
        try {
          top.onBack();
        } catch (error) {
          if (bridge) bridge.backWasHandled = false;
          console.error('Error in back handler:', error);
        }
      }
    }
  }, []);

  const handleMenu = useCallback(() => {
    if (onMenuRef.current) {
      onMenuRef.current();
    }
  }, []);

  // Выставляем triggerBack в мост. Обёртка после вызова должна читать backWasHandled и не вызывать goBack(), если true.
  useEffect(() => {
    const triggerBack = () => {
      try {
        const bridge = window.RyaskovNativeBridge;
        if (!bridge) return;
        bridge.backWasHandled = false;
        const stack = backStackRef.current;
        const top = stack.length > 0 ? stack[stack.length - 1] : null;
        if (top) {
          bridge.backWasHandled = true;
          try {
            top.onBack();
          } catch (error) {
            bridge.backWasHandled = false;
            console.error('Error in back handler:', error);
          }
        }
      } catch (error) {
        console.error('Error in triggerBack:', error);
        const bridge = window.RyaskovNativeBridge;
        if (bridge) bridge.backWasHandled = false;
      }
    };
    const bridge = (window.RyaskovNativeBridge = window.RyaskovNativeBridge || {});
    bridge.triggerBack = triggerBack;
    return () => {
      if (window.RyaskovNativeBridge === bridge) {
        delete window.RyaskovNativeBridge.triggerBack;
        delete window.RyaskovNativeBridge.backWasHandled;
      }
    };
  }, []);

  // ОТКЛЮЧЕНО: Синхронизация с браузерной кнопкой (вызывала проблемы)
  // Теперь работает только визуальная кнопка "Назад" в интерфейсе

  // Мемоизация value предотвращает лишние ре-рендеры потребителей и циклы
  // из-за эффектов с зависимостью от контекста
  const value = useMemo(
    () => ({ showBack, pushBackLayer, popBackLayer, hideBack, showMenu, hideMenu, state }),
    [state]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
      <GlobalNavButtons
        showBack={state.back.show}
        onBack={handleBack}
        backTransparent={state.back.transparent}
        showMenu={state.menu.show}
        onMenu={handleMenu}
        menuTransparent={state.menu.transparent}
      />
    </NavigationContext.Provider>
  );
};

export default NavigationContext;
